import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { AppLayout } from '@/components/layout/AppLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Search, Building2, Bed, Bath, MapPin, Heart } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-KE', {
    style: 'currency',
    currency: 'KES',
    minimumFractionDigits: 0,
  }).format(amount);
};

export default function HomesPage() {
  const { token } = useAuth();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [propertyType, setPropertyType] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchProperties();
  }, [propertyType]);

  const fetchProperties = async () => {
    try {
      const params = propertyType !== 'all' ? `?property_type=${propertyType}` : '';
      const response = await axios.get(`${API_URL}/api/properties${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProperties(response.data.properties || []);
    } catch (error) {
      console.error('Properties fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredProperties = properties.filter(p => 
    p.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.location.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <AppLayout title="Jambo Homes" showSearch>
      <div className="p-4 lg:p-6">
        {/* Hero Banner */}
        <div className="relative rounded-2xl overflow-hidden mb-6 h-48 lg:h-64">
          <img 
            src="https://images.pexels.com/photos/3754595/pexels-photo-3754595.jpeg?w=1200"
            alt="Jambo Homes"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/60 to-transparent flex items-center">
            <div className="p-6 lg:p-10">
              <h2 className="text-2xl lg:text-4xl font-bold text-white mb-2">Find Your Dream Home</h2>
              <p className="text-white/80 text-sm lg:text-base">Buy, rent, or invest in premium properties</p>
            </div>
          </div>
        </div>

        {/* Search */}
        <div className="mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input 
              placeholder="Search by location or property name..." 
              className="pl-10"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              data-testid="property-search"
            />
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={propertyType} onValueChange={setPropertyType} className="mb-6">
          <TabsList className="w-full max-w-sm">
            <TabsTrigger value="all" className="flex-1" data-testid="tab-all">All</TabsTrigger>
            <TabsTrigger value="sale" className="flex-1" data-testid="tab-sale">For Sale</TabsTrigger>
            <TabsTrigger value="rent" className="flex-1" data-testid="tab-rent">For Rent</TabsTrigger>
          </TabsList>
        </Tabs>

        {/* Properties Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {loading ? (
            Array(6).fill(0).map((_, i) => (
              <Card key={i} className="overflow-hidden">
                <Skeleton className="h-48" />
                <CardContent className="p-4">
                  <Skeleton className="h-5 w-3/4 mb-2" />
                  <Skeleton className="h-4 w-1/2 mb-4" />
                  <Skeleton className="h-6 w-1/3" />
                </CardContent>
              </Card>
            ))
          ) : filteredProperties.length ? (
            filteredProperties.map((property) => (
              <Card 
                key={property.id} 
                className="overflow-hidden card-hover property-card group"
                data-testid={`property-card-${property.id}`}
              >
                <div className="h-48 overflow-hidden bg-muted relative">
                  <img 
                    src={property.image_url || 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=600'}
                    alt={property.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute top-3 left-3">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      property.property_type === 'sale' 
                        ? 'bg-primary text-white' 
                        : 'bg-amber-500 text-white'
                    }`}>
                      For {property.property_type === 'sale' ? 'Sale' : 'Rent'}
                    </span>
                  </div>
                  <button 
                    className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/80 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                    data-testid={`wishlist-property-${property.id}`}
                  >
                    <Heart className="h-4 w-4" />
                  </button>
                </div>
                <CardContent className="p-4">
                  <h3 className="font-semibold text-lg mb-1 truncate">{property.title}</h3>
                  <div className="flex items-center gap-1 text-muted-foreground text-sm mb-3">
                    <MapPin className="h-4 w-4" />
                    <span className="truncate">{property.location}</span>
                  </div>
                  <div className="flex items-center gap-4 text-sm text-muted-foreground mb-4">
                    <span className="flex items-center gap-1">
                      <Bed className="h-4 w-4" /> {property.bedrooms} bed
                    </span>
                    <span className="flex items-center gap-1">
                      <Bath className="h-4 w-4" /> {property.bathrooms} bath
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <p className="text-primary font-bold text-lg price-tag">
                      {formatCurrency(property.price)}
                      {property.property_type === 'rent' && <span className="text-sm font-normal">/mo</span>}
                    </p>
                    <Button size="sm" className="rounded-full" data-testid={`view-property-${property.id}`}>
                      View Details
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))
          ) : (
            <div className="col-span-full py-20 text-center text-muted-foreground">
              <Building2 className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No properties found</p>
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
}
